import { CheckCircleIcon, InfoIcon, QuestionMarkIcon, TimeCircleIcon } from '@systemeio/ui/icons';
import { FormInput, FormInputSearch, Popover, RadioGroup, RadioGroupDataInterface } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import EmailTemplateSelector from 'modules/email/components/email-template-selector/email-template-selector';
import { useGetEmailTemplates } from 'modules/email/hooks/use-get-email-templates';
import { getEmailStatisticsUrl } from 'modules/mailing/email-statistics/utils/get-email-statistics-url';
import { CreateEmailInterface } from 'modules/mailing/types/mailing-interface';
import { createNewsletter, duplicateNewsletter, NEWSLETTERS_API, removeNewsletter, resendNewsletter } from 'modules/newsletters/single/api/newsletters-api';
import ResendNewsletterModalWithoutButton from 'modules/newsletters/single/components/resend-newsletter-modal/resend-newsletter-modal-without-buttons';
import { getNewsletterSingleColumnTitle } from 'modules/newsletters/single/configs/newsletter-single-columns';
import { NewslettersLoco } from 'modules/newsletters/single/constants/newsletters-loco';
import { useNewsLetters } from 'modules/newsletters/single/hooks/use-newsletter';
import { NewsletterCreateErrorsInterface, NewsletterErrorResponseContentInterface } from 'modules/newsletters/single/types/error-response-content-interface';
import { NewsletterFilterInterface } from 'modules/newsletters/single/types/newsletter-filter-interface';
import { NewsletterId, NewsletterType } from 'modules/newsletters/single/types/newsletter-interface';
import { getNewslettersEditorNextjsUrl } from 'modules/newsletters/single/utils/get-newsletters-editor-nextjs-url';
import { InterruptSplitTest } from 'modules/newsletters/split-test/components/interrupt-split-test';
import { getNewsletterSplitTestColumnTitle, getNewsletterSplitTestStatus } from 'modules/newsletters/split-test/configs/newsletter-split-test-columns';
import { MailingSplitTestStatusTypeEnum } from 'modules/newsletters/split-test/enum/mailing-split-test-status-type-enum';
import { getSplitTestNewsletterStatisticsUrl } from 'modules/newsletters/split-test/utils/get-split-test-newsletter-statistics-url';
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum';
import { getShareUrl } from 'modules/share/utils/get-share-url';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ConfirmationModal from 'shared/components/confirmation-modal';
import { CreateDataType } from 'shared/components/create-modal';
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options';
import LinkShareModal from 'shared/components/link-share-modal';
import ListingLayout from 'shared/components/listing-layout';
import SenderEmailSelect from 'shared/components/sender-email-select';
import { ListOfItemsCell } from 'shared/components/table/cells/list-of-items-cell';
import { TextWithTooltipCell } from 'shared/components/table/cells/text-with-tooltip-cell';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { isValidEmail } from 'shared/utils/email-validation';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getPercentAndAmountStatistic } from 'shared/utils/get-percent-and-amount-statistic';
import { MailingStatsWrapper } from 'modules/campaigns/campaign-steps/components/mailing-stats-wrapper';
const defaultValues: CreateEmailInterface = {
  subject: '',
  fromName: '',
  fromEmail: '',
  useNewEditor: true,
  template: undefined
};
const defaultErrors = {
  subject: '',
  fromName: '',
  fromEmail: ''
};
const defaultFilters: NewsletterFilterInterface = {
  search: ''
};
const Newsletter: NextPage = () => {
  const {
    user
  } = useUser();
  const router = useRouter();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters);
  const updateData = useUpdateData({
    api: NEWSLETTERS_API,
    constantApi: [`${NEWSLETTERS_API}/templates`],
    setPagination
  });
  const [errors, setErrors] = useState<NewsletterCreateErrorsInterface>(defaultErrors);
  const [preLoadedValues, setPreLoadedValues] = useState<CreateEmailInterface>(defaultValues);
  const {
    data: emailOptions
  } = useEmailOptions();
  const {
    templates
  } = useGetEmailTemplates();
  useEffect(() => {
    if (templates?.length) {
      setPreLoadedValues(prev => ({
        ...prev,
        template: templates[0]?.id
      }));
    }
  }, [templates]);
  useEffect(() => {
    if (emailOptions) {
      setPreLoadedValues(prev_0 => ({
        ...prev_0,
        fromEmail: prev_0.fromEmail || emailOptions.senderEmailAddress,
        fromName: prev_0.fromName || emailOptions.senderName
      }));
    }
  }, [emailOptions]);
  const {
    t
  } = useLocoTranslation();
  const {
    newsletters,
    counter,
    mutate
  } = useNewsLetters({
    limit,
    pagination,
    filter
  });
  const columns: ColumnDefinitionInterface<NewsletterType>[] = [{
    key: 'title',
    header: t('dashboard.newsletter.newsletter_title'),
    widthPercent: 35,
    onRender: newsletter => <>
          {newsletter.mailingSplitTest ? getNewsletterSplitTestColumnTitle(newsletter, t) : getNewsletterSingleColumnTitle(newsletter, t)}
        </>
  }, {
    key: 'connectedTags',
    header: t('dashboard.newsletter.connected_tags'),
    widthPercent: 20,
    onRender: newsletter_0 => <ListOfItemsCell items={newsletter_0.tags.map(({
      name
    }) => name)} entityName={t('dashboard.tag.title_plural')} />
  }, {
    key: 'emailsSentCount',
    header: <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.emails_sent_count')}
          </TextWithTooltipCell>
        </div>,
    widthPercent: 10,
    onRender: newsletter_1 => <TextWithTooltipCell wrapperClassName="justify-center">
          {newsletter_1.stats?.emailsSent}
        </TextWithTooltipCell>
  }, {
    key: 'numberOfViews',
    header: <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.number_of_views')}
          </TextWithTooltipCell>
        </div>,
    widthPercent: 10,
    onRender: newsletter_2 => <MailingStatsWrapper data={newsletter_2.stats}>
          {data => <div className="overflow-hidden lg:w-full">
              <TextWithTooltipCell wrapperClassName="justify-center">
                {getPercentAndAmountStatistic({
            numberOfActions: data.emailsOpened,
            totalNumber: data.emailsSent
          })}
              </TextWithTooltipCell>
            </div>}
        </MailingStatsWrapper>
  }, {
    key: 'numberOfClicks',
    header: <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.clicks_count')}
          </TextWithTooltipCell>
        </div>,
    widthPercent: 10,
    onRender: newsletter_3 => <MailingStatsWrapper data={newsletter_3.stats}>
          {data_0 => <div className="overflow-hidden lg:w-full">
              <TextWithTooltipCell wrapperClassName="justify-center">
                {getPercentAndAmountStatistic({
            numberOfActions: data_0.clicks,
            totalNumber: data_0.emailsSent
          })}
              </TextWithTooltipCell>
            </div>}
        </MailingStatsWrapper>
  }, {
    key: 'status',
    header: t('global.status'),
    widthPercent: 10,
    onRender: newsletter_4 => {
      return <>
            {newsletter_4.scheduledAt === null ? '' : <>
                {newsletter_4.isPending ? <div className="lg:ml-4">
                    <Popover label={<div dangerouslySetInnerHTML={{
              __html: t('dashboard.newsletter.status_pending')
            }} />} popoverClassName={'w-[250px] sm:w-[450px]'}>
                      {show => <InfoIcon className={`${show ? 'fill-brand-primary' : 'fill-fill-primary'} fill-fill-primary h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`} />}
                    </Popover>
                  </div> : <div className="flex items-center gap-1.5">
                    {newsletter_4.sent ? <>
                        {newsletter_4.mailingSplitTest ? getNewsletterSplitTestStatus(newsletter_4.mailingSplitTest, t) : <CheckCircleIcon className={`fill-green h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`} />}
                      </> : <TimeCircleIcon className={`fill-yellow h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`} />}
                    {dateTimeFormat(newsletter_4.scheduledAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone
            })}
                  </div>}
              </>}
          </>;
    }
  }];
  const onDuplicateNewsletter = async (newsletter_5: NewsletterType) => {
    const response = await duplicateNewsletter(newsletter_5.id);
    updateData(false, newsletters);
    router.push(getNewslettersEditorNextjsUrl(response.data.id));
  };
  const onCreate = async (data_1: CreateDataType<CreateEmailInterface>) => {
    try {
      if (!isValidEmail(data_1.fromEmail)) {
        setErrors(prev_1 => ({
          ...prev_1,
          fromEmail: t('dashboard.validation.email_not_valid')
        }));
        throw new Error();
      }
      const newsletterData = {
        mailing: {
          currentEmail: {
            fromEmail: data_1.fromEmail,
            fromName: data_1.fromName,
            subject: data_1.subject,
            useNewEditor: data_1.useNewEditor,
            template: data_1.template
          }
        }
      };
      await createNewsletter(newsletterData);
      await updateData(true, newsletters);
    } catch (e) {
      if (e instanceof BadRequest && e.errors.fields) {
        const errors_0 = e.errors as NewsletterErrorResponseContentInterface;
        const currentlMailing = !Array.isArray(errors_0.fields) ? errors_0.fields.mailing : undefined;
        if (currentlMailing) {
          setErrors({
            subject: currentlMailing?.currentEmail?.subject?.join('') || '',
            fromName: currentlMailing?.currentEmail?.fromName?.join('') || '',
            fromEmail: currentlMailing?.currentEmail?.fromEmail?.join('') || ''
          });
        }
      } else {
        throw e;
      }
      throw new Error();
    }
  };
  const onResendNewsletter = (newsletter_6: NewsletterType) => async (data_2: any) => {
    await resendNewsletter(newsletter_6.id, data_2);
    await updateData(false, newsletters);
  };
  const onRemoveNewsletter = async (newsletter_7: NewsletterType) => {
    await removeNewsletter(newsletter_7.id);
    await updateData(false, newsletters);
  };
  const onModalClose = () => setErrors(defaultErrors);
  const interruptSplitTest = async (newsletterId: NewsletterId) => {
    await mutate(data_3 => data_3 && {
      hasMore: data_3.hasMore,
      items: data_3.items.map(el => {
        if (el.id === newsletterId && el.mailingSplitTest) {
          return {
            ...el,
            mailingSplitTest: {
              ...el.mailingSplitTest,
              status: MailingSplitTestStatusTypeEnum.Interrupted
            }
          };
        } else {
          return el;
        }
      })
    }, false);
  };
  const templateTypeData: RadioGroupDataInterface<string>[] = [{
    id: 'new',
    caption: t('dashboard.email_template_selector.template_visual_editor')
  }, {
    id: 'old',
    caption: t('dashboard.email_template_selector.template_classic_editor')
  }];
  return <ListingLayout counter={counter} data={newsletters} title={<h1 className={'flex items-center gap-1.5'}>
          {t('dashboard.newsletter.newsletters')}
          <Popover label={t('header.top_menu.newsletters_tooltip_text')} popoverClassName={'w-[200px] sm:w-[350px]'}>
            {show_0 => <QuestionMarkIcon className={`${show_0 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary`} />}
          </Popover>
        </h1>} type={'compact'} filterData={{
    filterer: {
      filter,
      setFilter,
      defaultFilters
    },
    paginator: {
      pagination,
      setPagination
    },
    limiter: {
      limit,
      setLimit
    },
    filterable: [{
      field: 'search',
      onRender: (value, onChange) => <FormInputSearch key={'newsletter-search-filter'} className={'lg:w-72'} placeholder={t('global.search')} onClear={() => onChange('')} value={value} onChange={e_0 => onChange(e_0.target.value)} />
    }]
  }} createData={{
    caption: t('dashboard.newsletter.newsletter'),
    requiredFields: ['subject', 'fromName', 'fromEmail'],
    onAfterLeave: onModalClose,
    onCreate: data_4 => onCreate({
      ...preLoadedValues,
      ...data_4
    }),
    defaultValues: preLoadedValues,
    creatable: [{
      field: 'subject',
      onRender: (value_0, onChange_0) => <FormInput required key="newsletter-subject" label={<div className="flex items-center gap-2">
                    {t('dashboard.newsletter.subject_title')}{' '}
                    <Popover label={<div className="[&>a]:text-brand-primary" dangerouslySetInnerHTML={{
          __html: t('dashboard.newsletter.subject_title_tooltip')
        }} />} popoverClassName={'w-[160px] sm:w-[350px]'}>
                      {show_1 => <QuestionMarkIcon className={`${show_1 ? 'fill-brand-primary' : 'fill-fill-primary'} group-focus-visible:fill-brand-primary h-[16px] w-[16px]`} />}
                    </Popover>
                  </div>} placeholder={t('dashboard.newsletter.subject_title')} value={value_0} onChange={e_1 => onChange_0(e_1.target.value)} maxLength={256} error={errors.subject} />
    }, {
      field: 'fromName',
      onRender: (value_1, onChange_1) => <FormInput required key="newsletter-sender-name" label={t('dashboard.newsletter.sender_name_title')} value={value_1} onChange={e_2 => onChange_1(e_2.target.value)} maxLength={256} error={errors.fromName} />
    }, {
      field: 'fromEmail',
      onRender: (value_2, onChange_2) => <SenderEmailSelect required key="newsletter-sender-email" onChange={data_5 => data_5 && onChange_2(data_5)} value={value_2} error={errors.fromEmail} />
    }, {
      field: 'useNewEditor',
      onRender: (value_3, onChange_3, state, onChangeState) => <RadioGroup key="newsletter-editor-type" options={templateTypeData} selected={value_3 ? 'new' : 'old'} onChange={value_4 => {
        if (value_4 === 'old') {
          onChangeState({
            ...state,
            useNewEditor: false,
            template: undefined
          });
        } else onChangeState({
          ...state,
          useNewEditor: true,
          template: templates?.[0]?.id
        });
      }} />
    }, {
      field: 'template',
      shouldRender: (state_0: CreateEmailInterface) => state_0.useNewEditor,
      onRender: (value_5, onChange_4) => <EmailTemplateSelector key="template" onChange={onChange_4} templateId={value_5} templates={templates} />
    }]
  }}>
      <Table caption={t('dashboard.newsletter.newsletter').toLowerCase()} data={newsletters} columns={columns} limiter={{
      limit,
      setLimit
    }} paginator={{
      pagination,
      setPagination
    }} actions={{
      openLink: [{
        name: 'edit',
        caption: t('dashboard.actions.title.edit'),
        shouldRender: newsletter_8 => !newsletter_8.sent,
        setHref: newsletter_9 => getNewslettersEditorNextjsUrl(newsletter_9.id),
        isNext: true,
        openInNew: false
      }, {
        shouldRender: newsletter_10 => newsletter_10.sent,
        name: 'stats',
        caption: t('dashboard.actions.title.stats'),
        setHref: newsletter_11 => newsletter_11.mailingSplitTest ? getSplitTestNewsletterStatisticsUrl(newsletter_11.id) : getEmailStatisticsUrl(newsletter_11.mailing.id),
        isNext: true
      }],
      custom: [{
        shouldRender: newsletter_12 => newsletter_12.sent && !newsletter_12.mailingSplitTest,
        name: 'resend',
        caption: t('dashboard.newsletter.resend_title'),
        onActionRender: (opened, onClose, newsletter_13, key) => <ResendNewsletterModalWithoutButton opened={opened} subject={newsletter_13.mailing.currentEmail.subject} onClose={onClose} key={key} onResend={onResendNewsletter(newsletter_13)} />
      }, {
        name: 'share',
        caption: t('dashboard.actions.title.share'),
        shouldRender: newsletter_14 => !newsletter_14.mailingSplitTest,
        onActionRender: (opened_0, onClose_0, newsletter_15, key_0) => <LinkShareModal opened={opened_0} link={getShareUrl(ShareResourceEnum.Newsletter, newsletter_15.hash)} title={t('dashboard.newsletter.share_newsletter')} label={t('dashboard.newsletter.share_newsletter_text')} onClose={onClose_0} key={key_0} />
      }, {
        shouldRender: newsletter_16 => !newsletter_16.excludeRecipientsOfNewsletter && !newsletter_16.mailingSplitTest,
        name: 'duplicate',
        caption: t('dashboard.actions.title.duplicate'),
        onActionRender: (opened_1, onClose_1, newsletter_17, key_1) => <ConfirmationModal confirmationText={t('dashboard.newsletter.duplicate_confirm')} key={key_1} opened={opened_1} onClose={onClose_1} onConfirm={() => onDuplicateNewsletter(newsletter_17)} toastCaption={t('global.duplicated')} />
      }, {
        name: 'interruptSplitTest',
        caption: t('dashboard.newsletter_editor.split_test.interrupt.title'),
        onActionRender: (opened_2, onClose_2, newsletter_18, key_2) => <InterruptSplitTest mailingSplitTestId={newsletter_18.mailingSplitTest?.id} opened={opened_2} onClose={onClose_2} afterAbandon={() => interruptSplitTest(newsletter_18.id)} key={key_2} />,
        shouldRender: newsletter_19 => newsletter_19.mailingSplitTest?.status === MailingSplitTestStatusTypeEnum.Running
      }],
      remove: {
        shouldRender: newsletter_20 => newsletter_20.mailingSplitTest?.status !== MailingSplitTestStatusTypeEnum.Running,
        onChange: newsletter_21 => onRemoveNewsletter(newsletter_21)
      }
    }} />
    </ListingLayout>;
};
export const getServerSideProps = getBaseServerSideProps(NewslettersLoco, 'header.top_menu.newsletters');
export default Newsletter;